import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Plus, Pencil, Copy, Trash2, Eye } from "lucide-react";
import { toast } from "sonner";
import { Switch } from "../components/ui/switch";
import { api, errMsg } from "../lib/api";
import { tourPath } from "../lib/site";

export const STATUS_STYLE = { draft: "bg-slate-100 text-slate-700", needs_review: "bg-amber-100 text-amber-800", approved: "bg-blue-100 text-blue-800", published: "bg-emerald-100 text-emerald-800" };
export const STATUS_LABEL = { draft: "Draft", needs_review: "Needs Review", approved: "Approved", published: "Published" };

const StatCard = ({ label, value, testid }) => (
  <div className="rounded-2xl border border-slate-200 bg-white p-5"><p className="text-xs text-slate-500">{label}</p><p className="mt-1 font-display text-2xl font-extrabold" data-testid={testid}>{value ?? "–"}</p></div>
);

export default function AdminTours() {
  const [tours, setTours] = useState([]);
  const [stats, setStats] = useState({});
  const load = useCallback(() => {
    api.get("/admin/tours").then((r) => setTours(r.data));
    api.get("/admin/stats").then((r) => setStats(r.data));
  }, []);
  useEffect(() => { load(); }, [load]);

  const act = async (fn, msg) => { try { await fn(); toast.success(msg); load(); } catch (e) { toast.error(errMsg(e)); } };
  const del = (t) => { if (window.confirm(`Delete "${t.title}"? This cannot be undone.`)) act(() => api.delete(`/admin/tours/${t.slug}`), "Tour deleted"); };

  return (
    <div data-testid="admin-tours-page">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <h1 className="text-2xl sm:text-3xl">Tours</h1>
        <Link to="/admin/tours/new" className="btn-brand" data-testid="admin-add-tour-btn"><Plus className="h-4 w-4" />Add tour</Link>
      </div>
      <div className="mt-6 grid grid-cols-2 gap-4 md:grid-cols-5">
        <StatCard label="All tours" value={stats.tours} testid="stat-tours" />
        <StatCard label="Live on site" value={stats.published} testid="stat-published" />
        <StatCard label="Draft / needs review" value={stats.needs_review} testid="stat-review" />
        <StatCard label="Inquiries" value={stats.inquiries} testid="stat-inquiries" />
        <StatCard label="New inquiries" value={stats.new_inquiries} testid="stat-new-inquiries" />
      </div>
      <div className="mt-6 overflow-x-auto rounded-2xl border border-slate-200 bg-white">
        <table className="w-full min-w-[760px] text-sm">
          <thead className="bg-slate-50 text-left text-xs uppercase tracking-wider text-slate-500">
            <tr><th className="p-4">Tour</th><th className="p-4">Type</th><th className="p-4">Review status</th><th className="p-4">Live</th><th className="p-4 text-right">Actions</th></tr>
          </thead>
          <tbody>
            {tours.map((t) => (
              <tr key={t.slug} className="border-t border-slate-100" data-testid={`admin-tour-row-${t.slug}`}>
                <td className="p-4"><p className="font-semibold">{t.title}</p><p className="text-xs text-slate-500">{tourPath(t)}</p></td>
                <td className="p-4 capitalize">{t.type}</td>
                <td className="p-4"><span className={`rounded-full px-2.5 py-1 text-xs font-semibold ${STATUS_STYLE[t.review_status]}`}>{STATUS_LABEL[t.review_status]}</span></td>
                <td className="p-4"><Switch checked={t.is_published} onCheckedChange={(v) => act(() => api.patch(`/admin/tours/${t.slug}/publish`, { is_published: v }), v ? "Tour is now live" : "Tour unpublished")} data-testid={`admin-publish-toggle-${t.slug}`} /></td>
                <td className="p-4">
                  <div className="flex justify-end gap-1">
                    <a href={`${tourPath(t)}?preview=1`} target="_blank" rel="noreferrer" className="rounded-lg p-2 hover:bg-slate-100" title="Preview" data-testid={`admin-preview-${t.slug}`}><Eye className="h-4 w-4" /></a>
                    <Link to={`/admin/tours/${t.slug}`} className="rounded-lg p-2 hover:bg-slate-100" title="Edit" data-testid={`admin-edit-${t.slug}`}><Pencil className="h-4 w-4" /></Link>
                    <button onClick={() => act(() => api.post(`/admin/tours/${t.slug}/duplicate`), "Tour duplicated as draft")} className="rounded-lg p-2 hover:bg-slate-100" title="Duplicate" data-testid={`admin-duplicate-${t.slug}`}><Copy className="h-4 w-4" /></button>
                    <button onClick={() => del(t)} className="rounded-lg p-2 text-red-600 hover:bg-red-50" title="Delete" data-testid={`admin-delete-${t.slug}`}><Trash2 className="h-4 w-4" /></button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
